import L from 'leaflet';
import PlaceDataUtils from '../../utils/PlaceDataUtils';

/**
 * A special marker that represents a summary cluster at low zoom levels
 * This marker shows the total count and categories for an area without loading individual place details
 */
export class SummaryClusterMarker extends L.Marker {
    private totalCount: number;
    private isAtLimit: boolean;
    private categories: Map<string, number>;
    private centerLat: number;
    private centerLng: number;
    private calculatedCenterLat: number;
    private calculatedCenterLng: number;
    public readonly countOnly: boolean = true; // Flag to distinguish count-only clusters

    constructor(
        totalCount: number,
        isAtLimit: boolean,
        categories: Map<string, number>,
        centerLat: number,
        centerLng: number,
        calculatedCenterLat: number,
        calculatedCenterLng: number
    ) {
        super([centerLat, centerLng], {
            icon: SummaryClusterMarker.createClusterIcon(totalCount, isAtLimit, categories),
            interactive: true,
            zIndexOffset: 1000 // Ensure it appears above other markers
        });

        this.totalCount = totalCount;
        this.isAtLimit = isAtLimit;
        this.categories = categories;
        this.centerLat = centerLat;
        this.centerLng = centerLng;
        this.calculatedCenterLat = calculatedCenterLat;
        this.calculatedCenterLng = calculatedCenterLng;
    }

    /**
     * Creates a cluster icon for the summary marker
     */
    private static createClusterIcon(totalCount: number, isAtLimit: boolean, categories: Map<string, number>): L.DivIcon {
        // Convert categories map to sorted array
        const sortedCategories = Array.from(categories.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([category, count]) => ({
                category,
                count,
                iconPath: PlaceDataUtils.getPlaceCategoryDetail(category)?.iconPath || '/icons/places/Tree-16.svg'
            }));

        // Create HTML for cluster icon
        const clusterHtml = SummaryClusterMarker.createClusterHtml(totalCount, isAtLimit, sortedCategories);

        return L.divIcon({
            html: clusterHtml,
            className: 'place-marker-cluster',
            iconSize: L.point(50, 50),
            iconAnchor: [25, 25],
        });
    }

    /**
     * Creates the HTML content for cluster icon
     */
    private static createClusterHtml(totalCount: number, isAtLimit: boolean, categories: Array<{ category: string; count: number; iconPath: string }>): string {
        // Display '100+' if limit was reached, otherwise show actual count
        const displayCount = isAtLimit ? '100+' : totalCount.toString();
        
        let html = '<div class="place-cluster-container">';
        html += '<div class="place-cluster-circle">';
        html += `<div class="place-cluster-count">${displayCount}</div>`;
        html += '</div>';
        
        if (categories.length > 0) {
            html += '<div class="place-cluster-categories">';
            categories.forEach((cat) => {
                html += `<img src="${cat.iconPath}" alt="category" class="place-cluster-category-icon" />`;
            });
            html += '</div>';
        }
        
        html += '</div>';
        
        return html;
    }

    public getTotalCount(): number {
        return this.totalCount;
    }

    public getCategories(): Map<string, number> {
        return this.categories;
    }

    public getCalculatedCenter(): { lat: number; lng: number } {
        return {
            lat: this.calculatedCenterLat,
            lng: this.calculatedCenterLng
        };
    }
}
